import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, User as UserIcon, Bell, Sun, Moon, LogOut, ChevronDown } from 'lucide-react';
import { useUIStore } from '@/shared/stores/uiStore';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { ConfirmationModal } from '@/shared/components/molecules';
import { notificationsApi } from '@/features/notifications/api/notificationsApi';
import { notificationSound } from '@/shared/utils/soundUtil';
import './Header.tsx.css';

export const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { theme, toggleTheme } = useUIStore();
    const { user, logout, tenant } = useAuthStore();
    const { showToast } = useToastStore();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    
    const menuRef = useRef<HTMLDivElement>(null);
    const lastUnreadCountRef = useRef<number | null>(null);
    const knownIdsRef = useRef<Set<string>>(new Set());

    const isSuperAdmin = user?.role === 'SUPER_ADMIN';
    const schoolSlug = tenant?.slug || user?.school?.slug || (isSuperAdmin ? 'super-admin' : '');

    // Fetch notifications list, check unread status, and trigger chime if new unread arrives
    const checkUnreadNotifications = async (playSoundOnNew = false) => {
        if (!schoolSlug) return;
        // Skip duplicate fetch if user is already on the notifications page (page handles its own fetching)
        if (location.pathname.includes('/notifications') || location.pathname.endsWith('/notifications')) {
            return;
        }

        try {
            const response = await notificationsApi.getNotifications(schoolSlug, { page: 1, page_size: 10 }, isSuperAdmin);
            if (response.success && response.data) {
                const data = response.data as any;
                const items = Array.isArray(data) ? data : (data.items || []);
                const unreadItems = items.filter((n: any) => !n.is_read);
                const currentUnreadCount = unreadItems.length;
                setHasUnread(currentUnreadCount > 0);

                // If polling detected new unread items that weren't known before, play sound!
                if (playSoundOnNew && lastUnreadCountRef.current !== null) {
                    const hasFreshUnread = unreadItems.some((n: any) => !knownIdsRef.current.has(n.id));
                    if (hasFreshUnread && currentUnreadCount > lastUnreadCountRef.current) {
                        notificationSound.playNotificationSound('chime');
                    }
                }

                lastUnreadCountRef.current = currentUnreadCount;
                knownIdsRef.current = new Set(items.map((n: any) => n.id));
            }
        } catch (error) {
            console.error('Failed to check notifications:', error);
        }
    };

    // Check notifications count and poll every 30s in background
    useEffect(() => {
        if (schoolSlug) {
            checkUnreadNotifications(false);

            const interval = setInterval(() => {
                checkUnreadNotifications(true);
            }, 30000);

            const handleNotificationsUpdate = (event: any) => {
                if (event?.detail && typeof event.detail.hasUnread === 'boolean') {
                    setHasUnread(event.detail.hasUnread);
                } else {
                    checkUnreadNotifications(false);
                }
            };

            window.addEventListener('notifications_read_update', handleNotificationsUpdate);
            return () => {
                clearInterval(interval);
                window.removeEventListener('notifications_read_update', handleNotificationsUpdate);
            };
        }
    }, [schoolSlug]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        setIsMenuOpen(false);
        setIsLogoutModalOpen(true);
    };

    const confirmLogout = async () => {
        setIsLoggingOut(true);
        const currentUser = useAuthStore.getState().user;
        const isUserSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
        const tenantSlug = isUserSuperAdmin ? null : useAuthStore.getState().tenant?.slug;

        // Client-side only logout — no API call
        logout();
        showToast('Logged out successfully', 'success', 'Logout');
        setIsLogoutModalOpen(false);
        setIsLoggingOut(false);

        if (tenantSlug) {
            window.location.href = `/login?school_slug=${tenantSlug}`;
        } else {
            window.location.href = '/login';
        }
    };

    const getRoleLabel = (role?: string) => {
        if (!role) return 'User';
        const normalizedRole = role.toUpperCase();
        switch (normalizedRole) {
            case 'SUPER_ADMIN': return 'Super Administrator';
            case 'SCHOOL_ADMIN': return 'School Administrator';
            case 'EMPLOYEE': return 'Staff Member';
            default: return role;
        }
    };

    return (
        <header className="app-header">
            {/* Brand name only — no logo */}
            <div className="app-header__brand">
                <span className="app-header__brand-name">{isSuperAdmin ? 'Raabta' : (tenant?.name || 'Raabta')}</span>
            </div>

            <div className="app-header__center">
                {/* Search bar hidden for now */}
                {/* <div className="app-header__search">
                    <Search size={18} className="app-header__search-icon" />
                    <input type="text" placeholder="Search for tickets, users, or help..." className="app-header__search-input" />
                </div> */}
            </div>

            <div className="app-header__right">
                <button
                    className="app-header__action-btn theme-toggle"
                    onClick={toggleTheme}
                    title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                >
                    {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
                </button>
                {/* Notification icon for all roles */}
                <button 
                    className="app-header__action-btn" 
                    onClick={() => navigate('/notifications')} 
                    title="Notifications"
                    style={{ position: 'relative' }}
                >
                    <Bell size={20} />
                    {hasUnread && (
                        <span 
                            style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#ef4444',
                                border: '1.5px solid var(--nav-bg, #0A0E1A)',
                            }}
                        />
                    )}
                </button>
                <button
                    className="app-header__action-btn app-header__logout-btn"
                    onClick={handleLogout}
                    title="Logout"
                >
                    <LogOut size={18} />
                </button>
            </div>

            <ConfirmationModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                onConfirm={confirmLogout}
                title="Confirm Logout"
                message="Are you sure you want to logout? You will need to sign in again to access your account."
                confirmLabel={isLoggingOut ? "Logging out..." : "Logout"}
                variant="danger"
                isLoading={isLoggingOut}
            />
        </header>
    );
};
